import React, { useMemo } from 'react'; // Eliminados useState, useEffect, useRef innecesarios
import { Card, Form, Row, Col, Button, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalculator, faFire, faDrumstickBite, faBreadSlice, faTint,
    faInfoCircle, faBullseye, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

// --- Calculation Helpers (sin cambios) ---
export const calculateBMR = (weightKg, heightCm, ageYears, sex) => {
    if (!weightKg || !heightCm || !ageYears || !sex || Number(weightKg) <= 0 || Number(heightCm) <= 0 || Number(ageYears) <= 0) return null;
    const s = sex.toLowerCase();
    if (s === 'male') return (10 * Number(weightKg)) + (6.25 * Number(heightCm)) - (5 * Number(ageYears)) + 5;
    if (s === 'female') return (10 * Number(weightKg)) + (6.25 * Number(heightCm)) - (5 * Number(ageYears)) - 161;
    if (s === 'other') return (10 * Number(weightKg)) + (6.25 * Number(heightCm)) - (5 * Number(ageYears)) - 78; // Ejemplo para "other"
    return null; // Si el sexo no es válido
};
export const activityFactors = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9 };
export const calculateTDEE = (bmr, activityLevel) => {
    if (bmr === null || !activityLevel) return null;
    const factor = activityFactors[activityLevel?.toLowerCase()];
    return factor ? Math.round(bmr * factor) : null;
};
export const adjustCaloriesForGoal = (tdee, primary_goal, sex) => {
    if (tdee === null || !primary_goal) return tdee;
    const goal = primary_goal.toLowerCase();
    const goalAdjustments = { lose_weight: -500, maintain_weight: 0, gain_muscle: 300 };
    let adjustedTdee = tdee + (goalAdjustments[goal] || 0);
    console.log("adjustCaloriesForGoal1: ", tdee);
    console.log("adjustCaloriesForGoal2: ", goalAdjustments[goal]);
    console.log("adjustCaloriesForGoal3: ", adjustedTdee);

    const minCalories = sex?.toLowerCase() === 'female' ? 1200 : 1500;
    return Math.max(adjustedTdee, minCalories);
};
// --- End Calculation Helpers ---

const MACRO_CONFIG = {
    protein: { min: 10, max: 50, step: 1, label: "Protein", idealMin: 15, idealMax: 35, icon: faDrumstickBite, color: 'text-primary' },
    carb: { min: 20, max: 70, step: 1, label: "Carbs", idealMin: 40, idealMax: 65, icon: faBreadSlice, color: 'text-warning' },
    fat: { min: 15, max: 40, step: 1, label: "Fat", idealMin: 20, idealMax: 35, icon: faTint, color: 'text-danger' },
};

const CalorieMacroCalculator = ({
    // --- DATOS DEL PERFIL (PUEDEN SER EDITABLES O SOLO PARA MOSTRAR/CALCULAR SUGERENCIAS) ---
    profileData, // Objeto: { age, sex, height, weight, activityLevel, goal }

    // --- VALORES ACTUALES DE CALORÍAS Y MACROS A MOSTRAR Y EDITAR (CONTROLADOS POR EL PADRE) ---
    currentCalories,
    currentProteinPercent,
    currentCarbPercent,
    currentFatPercent,

    // --- CALLBACKS ---
    onTargetsChange, // ({ calories, protein, carb, fat }) => {} // Para los targets editables
    onProfileDataChange, // (name, value) => {} // Si los inputs de perfil son editables aquí

    // --- CONFIGURACIÓN DE UI ---
    areProfileInputsEditable = false, // ¿Son editables los campos del perfil aquí?
    areTargetsEditable = true,        // ¿Son editables las calorías y macros aquí?
    showApplyButton = false,          // Mostrar un botón de "Aplicar" dentro de este componente
    onApplyButtonClick,                 // Función para el botón de aplicar
    cardHeaderTitle = "Daily Calorie & Macro Estimator",
    cardHeaderIcon = faCalculator,
    showSuggestionsBasedOnProfile = true, // Mostrar BMR, TDEE, etc.
}) => {

    // --- CÁLCULOS DE SUGERENCIAS VISUALES (BMR, TDEE) ---
    const calculatedBMR = useMemo(() => {
        if (!profileData || !profileData.age || !profileData.sex || !profileData.height || !profileData.weight) return null;
        return calculateBMR(parseFloat(profileData.weight), parseInt(profileData.height, 10), parseInt(profileData.age, 10), profileData.sex);
    }, [profileData]);

    const calculatedTDEE = useMemo(() => {
        if (!profileData || !calculatedBMR) return null;
        return calculateTDEE(calculatedBMR, profileData.activityLevel);
    }, [calculatedBMR, profileData?.activityLevel]);

    const suggestedTargetCaloriesDisplay = useMemo(() => {
        if (!profileData || !calculatedTDEE) return 0;
        return adjustCaloriesForGoal(calculatedTDEE, profileData.primary_goal, profileData.sex);
    }, [calculatedTDEE, profileData?.primary_goal, profileData?.sex]);

    // --- CÁLCULOS DE GRAMOS ---
    const getGrams = (calories, percent, factor) => Math.round((calories * (percent / 100)) / factor);
    const proteinGrams = useMemo(() => getGrams(currentCalories, currentProteinPercent, 4), [currentCalories, currentProteinPercent]);
    const carbGrams = useMemo(() => getGrams(currentCalories, currentCarbPercent, 4), [currentCalories, currentCarbPercent]);
    const fatGrams = useMemo(() => getGrams(currentCalories, currentFatPercent, 9), [currentCalories, currentFatPercent]);

    // --- VALIDACIÓN ---
    const calorieValidationError = useMemo(() => {
        if (currentCalories < 1000 || currentCalories > 6000) return "Target Calories should be 1000-6000.";
        return '';
    }, [currentCalories]);

    const totalMacroPercent = useMemo(() => Math.round(
        (Number(currentProteinPercent) || 0) +
        (Number(currentCarbPercent) || 0) +
        (Number(currentFatPercent) || 0)
    ), [currentProteinPercent, currentCarbPercent, currentFatPercent]);

    const macroSumError = useMemo(() => {
        if (totalMacroPercent !== 100) return `Percentages must sum to 100%. Current sum: ${totalMacroPercent}%.`;
        return '';
    }, [totalMacroPercent]);

    const individualMacroWarning = useMemo(() => {
        let msg = '';
        if (currentProteinPercent < MACRO_CONFIG.protein.idealMin || currentProteinPercent > MACRO_CONFIG.protein.idealMax) msg += `Protein ideally ${MACRO_CONFIG.protein.idealMin}-${MACRO_CONFIG.protein.idealMax}%. `;
        if (currentCarbPercent < MACRO_CONFIG.carb.idealMin || currentCarbPercent > MACRO_CONFIG.carb.idealMax) msg += `Carbs ideally ${MACRO_CONFIG.carb.idealMin}-${MACRO_CONFIG.carb.idealMax}%. `;
        if (currentFatPercent < MACRO_CONFIG.fat.idealMin || currentFatPercent > MACRO_CONFIG.fat.idealMax) msg += `Fat ideally ${MACRO_CONFIG.fat.idealMin}-${MACRO_CONFIG.fat.idealMax}%. `;
        return msg.trim();
    }, [currentProteinPercent, currentCarbPercent, currentFatPercent]);

    // --- HANDLERS (llaman a los callbacks del padre) ---
    const handleProfileFieldChange = (e) => {
        if (onProfileDataChange) {
            onProfileDataChange(e.target.name, e.target.value);
        }
    };

    const handleCalorieValueChange = (e) => {
        let newCalories = parseInt(e.target.value, 10);
        if (isNaN(newCalories)) newCalories = 0; // El padre decidirá si 0 es válido o lo reemplaza
        if (onTargetsChange) {
            onTargetsChange({
                calories: newCalories,
                protein: currentProteinPercent,
                carb: currentCarbPercent,
                fat: currentFatPercent,
            });
        }
    };

    const handleMacroValueChange = (macroName, valueStr) => {
        const newValue = parseInt(valueStr, 10);
        if (isNaN(newValue)) return; // Ignorar si no es número

        let { protein, carb, fat } = {
            protein: currentProteinPercent,
            carb: currentCarbPercent,
            fat: currentFatPercent,
        };

        if (macroName === 'protein') protein = newValue;
        else if (macroName === 'carb') carb = newValue;
        else if (macroName === 'fat') fat = newValue;

        // Asegurar que los valores estén dentro de los límites min/max del slider
        protein = Math.max(MACRO_CONFIG.protein.min, Math.min(MACRO_CONFIG.protein.max, protein));
        carb = Math.max(MACRO_CONFIG.carb.min, Math.min(MACRO_CONFIG.carb.max, carb));
        fat = Math.max(MACRO_CONFIG.fat.min, Math.min(MACRO_CONFIG.fat.max, fat));

        if (onTargetsChange) {
            onTargetsChange({ calories: currentCalories, protein, carb, fat });
        }
    };

    const profileFieldsComplete = profileData && profileData.age && profileData.sex && profileData.height && profileData.weight;

    return (
        <Card className="shadow-sm mb-4 calorie-macro-calculator">
            <Card.Header as="h5" className="bg-primary text-white d-flex align-items-center">
                <FontAwesomeIcon icon={cardHeaderIcon} className="me-2" /> {cardHeaderTitle}
            </Card.Header>
            <Card.Body>
                {areProfileInputsEditable && profileData && (
                    <Form>
                        <h6 className="text-secondary small-caps-header">1. Your Profile Data</h6>
                        <Row className="g-3 mb-3">
                            <Col md={6} lg={3}><Form.Group><Form.Label>Age</Form.Label><Form.Control type="number" name="age" value={profileData.age || ''} onChange={handleProfileFieldChange} placeholder="Years" min="1" /></Form.Group></Col>
                            <Col md={6} lg={3}><Form.Group><Form.Label>Sex</Form.Label><Form.Select name="sex" value={profileData.sex || ''} onChange={handleProfileFieldChange}><option value="">Select...</option><option value="male">Male</option><option value="female">Female</option></Form.Select></Form.Group></Col>
                            <Col md={6} lg={3}><Form.Group><Form.Label>Height (cm)</Form.Label><Form.Control type="number" name="height" value={profileData.height || ''} onChange={handleProfileFieldChange} placeholder="e.g., 175" min="50" /></Form.Group></Col>
                            <Col md={6} lg={3}><Form.Group><Form.Label>Weight (kg)</Form.Label><Form.Control type="number" name="weight" value={profileData.weight || ''} onChange={handleProfileFieldChange} placeholder="e.g., 70" step="0.1" min="10" /></Form.Group></Col>
                        </Row>
                        <Row className="g-3 mb-4">
                            <Col md={6}><Form.Group><Form.Label>Activity Level</Form.Label><Form.Select name="activityLevel" value={profileData.activityLevel || 'sedentary'} onChange={handleProfileFieldChange}>{Object.keys(activityFactors).map(key => <option key={key} value={key}>{key.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</option>)}</Form.Select></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Primary Goal</Form.Label><Form.Select name="goal" value={profileData.primary_goal || 'maintain_weight'} onChange={handleProfileFieldChange}><option value="maintain_weight">Maintain Weight</option><option value="lose_weight">Lose Weight</option><option value="gain_muscle">Gain Muscle/Weight</option></Form.Select></Form.Group></Col>
                        </Row>
                        <hr />
                    </Form>
                )}

                <h6 className="text-secondary mt-3 small-caps-header">{areProfileInputsEditable ? "2." : ""} Target Calories & Macros</h6>

                <Row className="text-center my-3 align-items-center">
                    <Col md={areTargetsEditable ? 7 : 12} className="mb-3 mb-md-0">
                        <div className="fs-1 fw-bolder text-success">{currentCalories > 0 ? currentCalories : "..."}</div>
                        <div className="text-muted small text-uppercase">TARGET CALORIES/DAY</div>
                        {showSuggestionsBasedOnProfile && profileFieldsComplete && (
                            <Form.Text muted>
                                BMR: {calculatedBMR ? Math.round(calculatedBMR) : "-"} kcal, TDEE: {calculatedTDEE || "-"} kcal.
                                {suggestedTargetCaloriesDisplay > 0 && (
                                    <><br />Suggestion for profile: <strong className="text-info">{suggestedTargetCaloriesDisplay} kcal</strong>.</>
                                )}
                            </Form.Text>
                        )}
                        {!profileFieldsComplete && showSuggestionsBasedOnProfile && <Form.Text muted>Complete profile to see suggestions.</Form.Text>}
                    </Col>
                    {areTargetsEditable && (
                        <Col md={5}>
                            <Form.Group controlId="targetCaloriesInput">
                                <Form.Label className="small mb-0 visually-hidden">Adjust Target Calories</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        aria-label="Adjust Target Calories"
                                        value={String(currentCalories > 0 ? currentCalories : '')}
                                        onChange={handleCalorieValueChange}
                                        min="1000" max="6000" step="25"
                                        placeholder="e.g. 2000"
                                    />
                                    <InputGroup.Text>kcal</InputGroup.Text>
                                </InputGroup>
                                {calorieValidationError && <Form.Text className="text-danger small d-block mt-1">{calorieValidationError}</Form.Text>}
                            </Form.Group>
                        </Col>
                    )}
                </Row>

                {currentCalories >= 1000 && !calorieValidationError && (
                    <>
                        <hr />
                        <h6 className="text-secondary mt-3 small-caps-header">Macronutrient Split
                            {areTargetsEditable && <span className={`ms-2 fw-bold ${totalMacroPercent !== 100 ? 'text-danger' : 'text-success'}`}>(Total: {totalMacroPercent}%)</span>}
                        </h6>
                        {macroSumError && areTargetsEditable && <Alert variant="danger" size="sm" className="mt-2 mb-3 py-1 px-2"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" /> {macroSumError}</Alert>}
                        {individualMacroWarning && areTargetsEditable && !macroSumError && <Alert variant="warning" size="sm" className="mt-0 mb-3 py-1 px-2"><FontAwesomeIcon icon={faInfoCircle} className="me-1" /> {individualMacroWarning}</Alert>}

                        {Object.entries(MACRO_CONFIG).map(([macroKey, config]) => {
                            let currentPercent, currentGrams;
                            if (macroKey === 'protein') { currentPercent = currentProteinPercent; currentGrams = proteinGrams; }
                            else if (macroKey === 'carb') { currentPercent = currentCarbPercent; currentGrams = carbGrams; }
                            else { currentPercent = currentFatPercent; currentGrams = fatGrams; }

                            return (
                                <Form.Group key={macroKey} as={Row} className="mb-3 align-items-center macro-slider-group">
                                    <Form.Label column xs={12} sm={3} className={`text-sm-end small mb-0 ${config.color}`}>
                                        <FontAwesomeIcon icon={config.icon} className="me-1" /> {config.label} ({currentGrams > 0 ? currentGrams : '-'}g)
                                    </Form.Label>
                                    {areTargetsEditable && <Col xs={2} sm={1} className="text-end pe-1 small text-muted">{config.min}%</Col>}
                                    <Col xs={areTargetsEditable ? 8 : 12} sm={areTargetsEditable ? 6 : 7} >
                                        <Form.Range
                                            min={config.min} max={config.max} step={config.step}
                                            value={currentPercent}
                                            onChange={(e) => handleMacroValueChange(macroKey, e.target.value)}
                                            disabled={!areTargetsEditable}
                                            aria-label={`${config.label} Percentage`}
                                            className={`macro-slider-${macroKey}`}
                                        />
                                    </Col>
                                    {areTargetsEditable && <Col xs={2} sm={1} className="text-start ps-1 small text-muted">{config.max}%</Col>}
                                    <Col xs={12} sm={areTargetsEditable ? 2 : 12} className={`mt-1 mt-sm-0 ${areTargetsEditable ? 'text-center' : 'text-end'}`}>
                                        <Form.Control type="text" value={`${currentPercent}%`} readOnly size="sm" className="text-center macro-percent-display" aria-label={`${config.label} Percentage Value`} />
                                    </Col>
                                </Form.Group>
                            );
                        })}
                    </>
                )}
                {currentCalories < 1000 && !calorieValidationError && areTargetsEditable && (
                    <Alert variant="warning" className="small mt-2">Enter valid Target Calories (min 1000) to adjust macros.</Alert>
                )}

                {showApplyButton && (
                    <Button
                        variant="success"
                        onClick={onApplyButtonClick}
                        className="w-100 mt-4"
                        disabled={!!calorieValidationError || !!macroSumError || currentCalories < 1000}
                    >
                        <FontAwesomeIcon icon={faBullseye} className="me-2" />
                        Apply These Targets
                    </Button>
                )}
                {!areTargetsEditable && (
                    <Alert variant="light" className="mt-3 small text-muted">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        These are your estimated targets. You can adjust them in a different section if needed.
                    </Alert>
                )}
            </Card.Body>
        </Card>
    );
};

CalorieMacroCalculator.propTypes = {
    profileData: PropTypes.shape({
        age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sex: PropTypes.string,
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        activityLevel: PropTypes.string,
        primary_goal: PropTypes.string,
        bmr: PropTypes.number, // Opcional, si se quiere mostrar BMR calculado
        tdee: PropTypes.number, // Opcional, si se quiere mostrar TDEE calculado
    }),
    currentCalories: PropTypes.number.isRequired,
    currentProteinPercent: PropTypes.number.isRequired,
    currentCarbPercent: PropTypes.number.isRequired,
    currentFatPercent: PropTypes.number.isRequired,
    onTargetsChange: PropTypes.func.isRequired,
    onProfileDataChange: PropTypes.func,
    areProfileInputsEditable: PropTypes.bool,
    areTargetsEditable: PropTypes.bool,
    showApplyButton: PropTypes.bool,
    onApplyButtonClick: PropTypes.func,
    cardHeaderTitle: PropTypes.string,
    cardHeaderIcon: PropTypes.object,
    showSuggestionsBasedOnProfile: PropTypes.bool,
};

export default CalorieMacroCalculator;